import { ReactNode, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface MailListItem {
  id: string;
  title: string;
  sender: string;
  preview?: string | null;
  date?: string | null;
  priority?: string | null;
  status?: string | null;
  meta?: string | null;
}
interface Props {
  title: string;
  items: MailListItem[];
  renderDetail: (id: string) => ReactNode;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  empty?: string;
  filters?: ReactNode;
  canLeave?: () => boolean;
  selectionKey?: string;
}
export function MailWorkspace({
  title,
  items,
  renderDetail,
  loading,
  error,
  onRetry,
  empty = "Nessun risultato",
  filters,
  selectionKey = "selected",
  canLeave,
}: Props) {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [width, setWidth] = useState(340);
  const listRef = useRef<HTMLDivElement>(null);
  const visible = items.filter((item) =>
    `${item.title} ${item.sender} ${item.preview || ""} ${item.meta || ""}`
      .toLocaleLowerCase("it")
      .includes(search.toLocaleLowerCase("it")),
  );
  const selected = params.get(selectionKey);
  const current = visible.find((item) => item.id === selected);
  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set(selectionKey, id);
    else next.delete(selectionKey);
    setParams(next);
  };
  return (
    <div className={`mail-workspace ${current ? "has-selection" : ""}`}>
      <section className="mail-list-pane" style={{ width }} aria-label={title}>
        <div className="mail-list-header">
          <div className="flex justify-between items-center gap-2">
            <h2>{title}</h2>
            <span className="text-muted-foreground text-xs">
              {visible.length}
            </span>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              aria-label={`Cerca in ${title}`}
              placeholder="Cerca"
              type="search"
              value={search}
              onChange={(e) => {
                if (
                  current &&
                  !`${current.title} ${current.sender} ${current.preview || ""} ${current.meta || ""}`
                    .toLocaleLowerCase("it")
                    .includes(e.target.value.toLocaleLowerCase("it")) &&
                  canLeave &&
                  !canLeave()
                )
                  return;
                setSearch(e.target.value);
              }}
              className="pl-8 rounded-lg"
            />
          </div>
          {filters && <div className="mail-filters">{filters}</div>}
        </div>
        <div className="mail-list-scroll" ref={listRef}>
          {error ? (
            <div className="mail-empty">
              <p>Impossibile caricare {title.toLowerCase()}</p>
              <Button onClick={onRetry} variant="outline">
                Riprova
              </Button>
            </div>
          ) : loading ? (
            <div className="mail-empty" role="status">
              Caricamento…
            </div>
          ) : visible.length === 0 ? (
            <div className="mail-empty">{empty}</div>
          ) : (
            visible.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={`mail-row ${current?.id === item.id ? "is-selected" : ""}`}
                aria-current={current?.id === item.id ? "true" : undefined}
                onClick={() => select(item.id)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowDown" && event.key !== "ArrowUp")
                    return;
                  const next = index + (event.key === "ArrowDown" ? 1 : -1);
                  if (next < 0 || next >= visible.length) return;
                  event.preventDefault();
                  select(visible[next].id);
                  const button =
                    listRef.current?.querySelectorAll<HTMLButtonElement>(
                      ".mail-row",
                    )[next];
                  button?.focus();
                  button?.scrollIntoView?.({ block: "nearest" });
                }}
              >
                <span
                  className={`mail-case-spine priority-${item.priority || "low"}`}
                />
                <span className="flex justify-between gap-3">
                  <span className="font-semibold truncate">{item.sender}</span>
                  <time className="shrink-0 text-[11px] mail-secondary">
                    {item.date
                      ? new Date(item.date).toLocaleDateString("it-IT", {
                          day: "numeric",
                          month: "short",
                        })
                      : ""}
                  </time>
                </span>
                <span className="block truncate mt-0.5 font-medium">
                  {item.title}
                </span>
                <span className="block line-clamp-2 mt-1 text-xs mail-secondary">
                  {item.preview || item.meta || "Apri la conversazione"}
                </span>
                {item.meta && item.preview && (
                  <span className="block truncate mt-1 text-[11px] mail-secondary">
                    {item.meta}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
        <footer className="mail-list-footer">
          {visible.length}{" "}
          {title === "Bozze"
            ? "bozze"
            : title === "Archivio"
              ? "messaggi"
              : "conversazioni"}
        </footer>
      </section>
      <div
        className="mail-resizer"
        role="separator"
        aria-label="Larghezza lista"
        aria-orientation="vertical"
        aria-valuemin={260}
        aria-valuemax={480}
        aria-valuenow={width}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            setWidth((value) =>
              Math.max(
                260,
                Math.min(480, value + (e.key === "ArrowRight" ? 20 : -20)),
              ),
            );
          }
        }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
          const left =
            e.currentTarget.parentElement!.getBoundingClientRect().left;
          setWidth(Math.max(260, Math.min(480, e.clientX - left)));
        }}
        onPointerUp={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId))
            e.currentTarget.releasePointerCapture(e.pointerId);
        }}
      />
      <section className="mail-reading-pane" aria-label="Pannello di lettura">
        {current ? (
          <>
            <div className="mail-mobile-back">
              <Button variant="ghost" onClick={() => select(null)}>
                <ArrowLeft className="size-4" />
                {title}
              </Button>
            </div>
            <div key={current.id} className="mail-detail">
              {renderDetail(current.id)}
            </div>
          </>
        ) : (
          <div className="mail-empty h-full">
            <Mail className="size-12 opacity-30" />
            <p>
              Seleziona {title === "Bozze" ? "una bozza" : "una conversazione"}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
